/**
 * LiveStreamX — shared types, consumed (mostly as type-only) by apps/api, apps/web
 * and services/streaming-controller.
 */

/* ------------------------------------------------------------------ */
/* Enums / constants                                                    */
/* ------------------------------------------------------------------ */

export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  TENANT = 'TENANT',
}

export const ALL_ROLES = [UserRole.SUPER_ADMIN, UserRole.TENANT] as const;

export type StreamStatus =
  | 'LIVE'
  | 'CONNECTING'
  | 'RECONNECTING'
  | 'OFFLINE'
  | 'ERROR';

export const STREAM_STATUSES: readonly StreamStatus[] = [
  'LIVE',
  'CONNECTING',
  'RECONNECTING',
  'OFFLINE',
  'ERROR',
];

export type DestinationStatus =
  | 'CONNECTED'
  | 'CONNECTING'
  | 'RECONNECTING'
  | 'FAILED'
  | 'DISABLED';

export const DESTINATION_STATUSES: readonly DestinationStatus[] = [
  'CONNECTED',
  'CONNECTING',
  'RECONNECTING',
  'FAILED',
  'DISABLED',
];

export type SubscriptionStatus =
  | 'ACTIVE'
  | 'PAST_DUE'
  | 'CANCELLED'
  | 'EXPIRED'
  | 'SUSPENDED';

export const SUBSCRIPTION_STATUSES: readonly SubscriptionStatus[] = [
  'ACTIVE',
  'PAST_DUE',
  'CANCELLED',
  'EXPIRED',
  'SUSPENDED',
];

export type BillingCycle = 'DAILY' | 'MONTHLY' | 'YEARLY';

/** Every cycle the API accepts, in the order the UI offers them. */
export const BILLING_CYCLES: readonly BillingCycle[] = ['DAILY', 'MONTHLY', 'YEARLY'];

/** Days of service one cycle buys. */
export const BILLING_CYCLE_DAYS: Record<BillingCycle, number> = {
  DAILY: 1,
  MONTHLY: 30,
  YEARLY: 365,
};

/**
 * Plan prices arrive as strings (TypeORM `decimal` columns) or numbers (JSON
 * DTOs), so the helper below takes either and normalises with `Number()`.
 */
export interface PlanPrices {
  priceDay: string | number | null;
  priceMonth: string | number;
  priceYear: string | number | null;
}

/**
 * List price of one cycle. Falls back to the monthly rate — ÷30 for a day, ×12
 * for a year — when the cycle has no price of its own, so a plan created before
 * daily pricing existed still quotes a sane figure instead of ₹0.
 */
export function priceForCycle(plan: PlanPrices, cycle: BillingCycle): number {
  const month = Number(plan.priceMonth) || 0;
  if (cycle === 'DAILY') return Number(plan.priceDay ?? month / 30) || 0;
  if (cycle === 'YEARLY') return Number(plan.priceYear ?? month * 12) || 0;
  return month;
}

/** The parts of a plan that decide which cycle it is sold on and how long that lasts. */
export interface PlanCycleInfo extends PlanPrices {
  billingCycle?: BillingCycle | null;
  /** How many cycles one purchase buys (3 = a quarter when the cycle is MONTHLY). */
  cycleCount?: number | null;
  priceDay: string | number | null;
  priceYear: string | number | null;
}

/**
 * Cycle a plan is sold on. Plans created before `billing_cycle` existed declare
 * it implicitly through the price they carry (day > year > month).
 */
export function planCycleOf(plan: PlanCycleInfo): BillingCycle {
  if (plan.billingCycle) return plan.billingCycle;
  if (plan.priceDay !== null && plan.priceDay !== undefined) return 'DAILY';
  if (plan.priceYear !== null && plan.priceYear !== undefined) return 'YEARLY';
  return 'MONTHLY';
}

/** Days of service one purchase buys: cycle length × how many cycles. */
export function planSpanDays(plan: PlanCycleInfo): number {
  const count = Math.max(1, Math.floor(Number(plan.cycleCount) || 1));
  return BILLING_CYCLE_DAYS[planCycleOf(plan)] * count;
}

export type StreamPlatform =
  | 'youtube'
  | 'facebook'
  | 'twitch'
  | 'linkedin'
  | 'custom_rtmp';

export const STREAM_PLATFORMS: readonly StreamPlatform[] = [
  'youtube',
  'facebook',
  'twitch',
  'linkedin',
  'custom_rtmp',
];

export type UserStatus = 'ACTIVE' | 'BANNED' | 'SUSPENDED' | 'PENDING_VERIFICATION';

export type TenantStatus = 'ACTIVE' | 'SUSPENDED';

export type StreamEventType =
  | 'CREATED'
  | 'UPDATED'
  | 'DELETED'
  | 'STARTED'
  | 'STOPPED'
  | 'FAILED'
  | 'KEY_REGENERATED';

export type NotificationType =
  | 'STREAM_STARTED'
  | 'STREAM_STOPPED'
  | 'DESTINATION_CONNECTED'
  | 'DESTINATION_FAILED'
  | 'SUBSCRIPTION_EXPIRING'
  | 'PAYMENT_SUCCESS'
  | 'PAYMENT_FAILED';

/* ------------------------------------------------------------------ */
/* Users / Auth                                                        */
/* ------------------------------------------------------------------ */

export interface UserDto {
  id: string;
  email: string;
  role: UserRole;
  status: UserStatus;
  emailVerifiedAt: string | null;
  createdAt: string;
}

export interface AuthResponseDto {
  user: UserDto;
  tenant: TenantDto | null;
  /** True when a tenant is active and not suspended. */
  onboardingComplete: boolean;
}

export interface SessionDto {
  id: string;
  createdAt: string;
  lastActiveAt: string;
  ipAddress: string | null;
  userAgent: string | null;
  current: boolean;
}

/* ------------------------------------------------------------------ */
/* Tenants                                                             */
/* ------------------------------------------------------------------ */

export interface TenantDto {
  id: string;
  name: string;
  slug: string;
  status: TenantStatus;
  subscriptionStatus: SubscriptionStatus | null;
  planName: string | null;
  subscriptionExpiresAt: string | null;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Plans & subscriptions                                               */
/* ------------------------------------------------------------------ */

export interface PlanDto {
  id: string;
  name: string;
  description: string | null;
  priceMonth: number;
  priceYear: number | null;
  /** Price of a 1-day sale. Null = not sold per day (falls back to month ÷ 30). */
  priceDay: number | null;
  /** Cycle the plan is sold on. Null = infer from the price fields (`planCycleOf`). */
  billingCycle: BillingCycle | null;
  /** How many cycles one purchase buys. 1 = one day / month / year. */
  cycleCount: number;
  currency: string;
  maxStreams: number;
  maxDestinationsPerStream: number;
  maxTotalDestinations: number;
  maxStorageGb: number;
  maxBandwidthGb: number;
  recordingEnabled: boolean;
  /**
   * Ingest ceiling (Mbps). A publisher above this is disconnected — the plan
   * never accepts more than the tenant paid for. 0 = unlimited.
   */
  maxInboundBitrateMbps: number;
  /**
   * Egress ceiling (Mbps) per destination relay; each destination is re-encoded
   * down to it. 0 = unlimited.
   */
  maxOutboundBitrateMbps: number;
  /** Max concurrent viewers allowed (null = unlimited). */
  viewLimit: number | null;
  status: 'ACTIVE' | 'ARCHIVED';
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Streams & destinations                                              */
/* ------------------------------------------------------------------ */

export interface StreamDto {
  id: string;
  /** URL slug (stream name in URL form), e.g. `my-first-stream`; UUID fallback for legacy rows. */
  slug: string;
  name: string;
  description: string | null;
  status: StreamStatus;
  streamKeyLast4: string;
  recordingEnabled: boolean;
  thumbnailUrl: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
  updatedAt: string;
  rtmpUrl: string;
  /**
   * SRT ingest address. MediaMTX carries the path in the `streamid` query
   * parameter, so this has no trailing path segment — the encoder appends the
   * stream key to `streamid`, exactly as it appends it to the RTMP path.
   */
  srtUrl: string;
  hlsUrl: string | null;
  playerUrl: string | null;
  embedUrl: string | null;
  /** Only populated by POST /streams after creation. */
  streamKey?: string;
  /**
   * Hybrid billing state for this stream. `planId`/`renewsAt` are null until the
   * stream is first renewed or the tenant's active plan is backfilled onto it.
   */
  plan?: StreamPlanDto | null;
  /** ISO date this stream's plan next renews; null when never renewed. */
  renewsAt?: string | null;
  /** What was last actually paid for this stream, in `lastPaidCurrency`. */
  lastPaidAmount?: number | null;
  lastPaidCurrency?: string | null;
  billingCycle?: BillingCycle | null;
  /**
   * Billing gate for this stream. Only `ACTIVE` may publish; every other state
   * stays listed (URLs and key untouched) so the tenant can renew it.
   */
  planState?: StreamPlanState;
}

/**
 * Billing gate for one stream, evaluated per request:
 *  - `ACTIVE`       — a live subscription pays for it; ingest and go-live work.
 *  - `DEACTIVATED`  — the plan was switched off/cancelled, or the plan row is gone.
 *  - `EXPIRED`      — the paid period lapsed; the plan itself is still on the shelf.
 *  - `NO_PLAN`      — no subscription ever covered this stream.
 * `DEACTIVATED`/`EXPIRED`/`NO_PLAN` keep the stream visible so the tenant can
 * renew, but the API refuses ingest and go-live until it is `ACTIVE` again.
 */
export type StreamPlanState = 'ACTIVE' | 'DEACTIVATED' | 'EXPIRED' | 'NO_PLAN';

/** Card/detail label per state — shared so API reasons and UI labels agree. */
export const PLAN_STATE_LABEL: Record<StreamPlanState, string> = {
  ACTIVE: 'Active',
  DEACTIVATED: 'Deactivated',
  EXPIRED: 'Plan expired',
  NO_PLAN: 'No plan',
};

/** Why a non-ACTIVE stream is blocked, shown in the go-live popup. */
export const PLAN_BLOCK_MESSAGE: Record<Exclude<StreamPlanState, 'ACTIVE'>, string> = {
  DEACTIVATED:
    'This stream is deactivated — its plan was switched off or cancelled. Renew the plan to stream again.',
  EXPIRED:
    'This stream’s plan has expired. The stream URL and key are kept as they are; renew the plan to start streaming again.',
  NO_PLAN: 'This stream has no plan yet, so streaming is blocked. Choose a plan to enable it.',
};

/** `null` when the stream may stream, otherwise the reason to show the tenant. */
export function planBlockMessage(state: StreamPlanState | undefined): string | null {
  if (!state || state === 'ACTIVE') return null;
  return PLAN_BLOCK_MESSAGE[state];
}

/** Plan summary attached to a stream for display and the renew popup. */
export interface StreamPlanDto {
  id: string;
  name: string;
  priceMonth: number;
  priceYear: number | null;
}

/**
 * One recorded file written by MediaMTX for a stream.
 *
 * MediaMTX owns the files — it creates one per publish session and deletes them
 * on `recordDeleteAfter` — so this is a read of the directory, not a table. The
 * DB column `stream_recordings` is therefore unused.
 */
export interface StreamRecordingDto {
  /** File name inside the stream's recording directory, e.g. `2026-10-03_09-00-00_000.mp4`. */
  name: string;
  sizeBytes: number;
  /** ISO mtime — when the file was last written (moves while a session is live). */
  recordedAt: string;
  /** Same-origin URL; nginx serves the shared recordings volume under this prefix. */
  url: string;
}

/**
 * The tenant's recording storage: plan quota against what is on disk.
 * `quotaGb <= 0` means the plan stores nothing (recording start is refused).
 */
export interface StorageQuotaDto {
  /** Plan storage allowance in GB (summed across active plans). */
  quotaGb: number;
  /** Bytes of recordings currently on disk for this tenant. */
  usedBytes: number;
  /** Bytes left before the quota is hit; never negative. */
  remainingBytes: number;
  /** Files older than this are deleted automatically (MediaMTX `recordDeleteAfter`). */
  retentionDays: number;
}

/**
 * One recording plus the stream it belongs to, for the tenant-wide recordings
 * page. A tenant may own many streams, so the file alone is not identifiable.
 */
export interface TenantRecordingDto extends StreamRecordingDto {
  streamId: string;
  streamName: string;
  /** URL slug of the stream, for a link back to its detail page. */
  streamSlug: string;
}

/**
 * One recording plus the tenant and stream it belongs to, for the admin page
 * that lists every recorded file across every tenant.
 */
export interface AdminRecordingDto extends TenantRecordingDto {
  tenantId: string;
  tenantName: string;
}

/**
 * Live ingest/egress metrics for a running stream, sampled from the MediaMTX
 * control API. `null` means "not measurable" (stream offline, or MediaMTX does
 * not report the field) — never `0`, so a UI can tell "offline" from "idle".
 */
export interface StreamLiveStatsDto {
  live: boolean;
  /** Video bitrate in kbps. MediaMTX meters bytes per path, not per track, so this is the full ingest rate. */
  videoKbps: number | null;
  /** e.g. `1920x1080`, or `null` while the muxer has not reported a video track. */
  resolution: string | null;
  /** e.g. `H264 High 4.1, AAC 44.1 kHz stereo`. */
  codecs: string | null;
  /** Just the video half of `codecs`, e.g. `H264 High 4.1`. */
  videoCodec: string | null;
  /** Just the audio half of `codecs`, e.g. `AAC 44.1 kHz stereo`. */
  audioCodec: string | null;
  /** `null` — MediaMTX exposes no per-track byte counter, so audio cannot be split out. */
  audioKbps: number | null;
  /** Concurrent readers on the path (HLS players + relays). */
  viewers: number | null;
  /** Publisher -> server, in kbps. */
  inputKbps: number | null;
  /** Server -> readers (HLS + relays), in kbps. */
  outputKbps: number | null;
}

/**
 * One viewer on a live stream, read from the MediaMTX HLS session list.
 *
 * MediaMTX opens a *session per HTTP request* (playlist poll, segment fetch), so
 * one browser produces many. They are collapsed per (IP, User-Agent) into a
 * single row: `transmittedBytes` is the sum over that viewer's open sessions
 * and `startedAt` is the oldest one still open.
 */
export interface StreamSessionDto {
  /** 1-based row number, oldest viewer first. */
  no: number;
  /** Playback protocol. */
  protocol: 'HLS' | 'RTMP';
  /** Viewer IP without the ephemeral port; `null` when MediaMTX reports none. */
  ip: string | null;
  /** Bytes MediaMTX has delivered to this viewer so far. */
  transmittedBytes: number;
  /** When the viewer's oldest still-open session began. */
  startedAt: string;
  userAgent: string | null;
}

/** One bucket of the Device Breakdown table. */
export interface ViewerDeviceDto {
  /** e.g. `Mobile`, `Desktop`, `Smart TV`, `Unknown`. */
  device: string;
  /** Viewers on this device right now. */
  viewers: number;
  /** Share of current viewers, 0-100, rounded to one decimal. */
  percentage: number;
}

/** One row of the Top Countries table. */
export interface ViewerCountryDto {
  /** Country name, or `Unknown` for a private/unmapped address. */
  country: string;
  /** Viewers from this country right now. */
  viewers: number;
}

/**
 * Audience breakdown for a live stream, from the MediaMTX HLS session list.
 *
 * Viewers are collapsed per (IP, User-Agent) exactly as in `StreamSessionDto`,
 * so a browser polling the playlist counts once rather than once per request.
 *
 * ponytail: the aggregates (peak / total views / avg watch time) are built by
 * an in-process sampler, so they reset on API restart and cover the current
 * session only. Upgrade path: fold these samples into `stream_health_logs`.
 */
export interface StreamViewerStatsDto {
  /** False when the path has no viewers right now (or the stream is offline). */
  live: boolean;
  /** Distinct viewers watching right now. */
  currentViewers: number;
  /** Highest `currentViewers` seen since this session started. */
  peakViewers: number;
  /** Distinct viewers seen since this session started. */
  totalViews: number;
  /** Mean watch time of finished viewer sessions, in seconds. */
  avgWatchSeconds: number | null;
  /** Device split of the *current* viewers, most-viewed first. */
  devices: ViewerDeviceDto[];
  /** Country split of the *current* viewers, most-viewed first. */
  countries: ViewerCountryDto[];
}

export interface DestinationDto {
  id: string;
  name: string;
  platform: StreamPlatform;
  rtmpUrl: string | null;
  status: DestinationStatus;
  enabled: boolean;
  lastConnectedAt: string | null;
  lastError: string | null;
  reconnectCount: number;
  bitrateKbps: number | null;
  fps: number | null;
  createdAt: string;
}

/* ------------------------------------------------------------------ */
/* Analytics / dashboard                                               */
/* ------------------------------------------------------------------ */

export interface TenantDashboardDto {
  liveStreams: number;
  totalStreams: number;
  activeDestinations: number;
  totalDestinations: number;
  bandwidthUsedGb: number;
  currentPlanName: string;
  subscriptionStatus: SubscriptionStatus | null;
  subscriptionExpiresAt: string | null;
  liveNow: StreamDto[];
  stats: DashboardStatsDto;
}

export interface DashboardStatsDto {
  totalSessions: number;
  totalStreamingMinutes: number;
  totalBandwidthGb: number;
  peakBitrateKbps: number;
}

export interface AnalyticsDto {
  totalStreamingMinutes: number;
  totalSessions: number;
  avgSessionMinutes: number;
  bandwidthUsedGb: number;
  peakBitrateKbps: number;
  avgBitrateKbps: number;
  destinationUptimePct: number;
  destinationFailures: number;
  daily: DailyPointDto[];
}

/** One bandwidth sample. `t` is the sample time in epoch milliseconds. */
export interface BandwidthPointDto {
  t: number;
  /** Ingest: bytes the encoder pushed to MediaMTX, in bits per second. */
  upKbps: number;
  /** Egress: bytes MediaMTX pushed to viewers, in bits per second. */
  downKbps: number;
}

export interface StreamBandwidthDto {
  live: boolean;
  sessionStartedAt: string | null;
  /** Upload + download over the most recent sample window. */
  currentKbps: number | null;
  /** Mean upload + download since the session started. */
  averageKbps: number | null;
  /** Highest upload + download seen since the session started. */
  peakKbps: number | null;
  /** Upload + download bytes moved since the session started. */
  totalBytes: number;
  /** Ingest half of `totalBytes`: bytes the encoder pushed to MediaMTX. */
  upBytes: number;
  /** Egress half of `totalBytes`: bytes MediaMTX pushed to viewers. */
  downBytes: number;
  uploadKbps: number | null;
  downloadKbps: number | null;
  dataPoints: number;
  points: BandwidthPointDto[];
}

export interface DailyPointDto {
  date: string;
  streamingMinutes: number;
  bandwidthGb: number;
  sessions: number;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/* ------------------------------------------------------------------ */
/* Admin                                                               */
/* ------------------------------------------------------------------ */

export interface AdminDashboardDto {
  totalTenants: number;
  activeTenants: number;
  totalStreams: number;
  liveStreams: number;
  totalDestinations: number;
  connectedDestinations: number;
  monthlyRevenue: number;
  activeSubscriptions: number;
  upcomingRenewals: UpcomingRenewalDto[];
  streamingServers: StreamingServerDto[];
}

/** Per-tenant streaming usage for admin reports. */
export interface ReportUsageRowDto {
  tenantName: string;
  planName: string | null;
  sessions: number;
  /** Total streamed duration in seconds. */
  durationSeconds: number;
  /** Total bytes transferred across all sessions. */
  bytes: number;
  /** Highest peak ingest bitrate (kbps) observed across sessions. */
  peakBitrateKbps: number | null;
}

/** Aggregated value for a single UTC day (`YYYY-MM-DD`). */
export interface ReportDailySeriesDto {
  date: string;
  sessions: number;
  durationSeconds: number;
}

/** Aggregated value for a single UTC month (`YYYY-MM`). */
export interface ReportMonthlyCountDto {
  month: string;
  count: number;
}

export interface ReportRevenueRowDto {
  month: string;
  amount: number;
  count: number;
}

export interface ReportPlanMixDto {
  planName: string;
  count: number;
}

export interface AdminReportsDto {
  usage: {
    totalSessions: number;
    totalDurationSeconds: number;
    totalBytes: number;
    avgPeakBitrateKbps: number | null;
    /** Sessions per UTC day for the last 30 days (zero-filled). */
    daily: ReportDailySeriesDto[];
    perTenant: ReportUsageRowDto[];
  };
  growth: {
    signupsPerMonth: ReportMonthlyCountDto[];
    tenantsByStatus: { status: string; count: number }[];
    subscriptionsByStatus: { status: string; count: number }[];
    planMix: ReportPlanMixDto[];
  };
  revenue: {
    monthly: ReportRevenueRowDto[];
    totalAmount: number;
  };
}

/** One row of the platform-wide transaction history (the `payments` ledger). */
export interface AdminPaymentRowDto {
  id: string;
  tenantId: string;
  tenantName: string;
  planName: string | null;
  billingCycle: string | null;
  /** Stream this payment renewed; plan-fallback rows name every stream on the plan. Null ⇒ "Stream not created". */
  streamName: string | null;
  /** DECIMAL column, coerced to a number for display only. */
  amount: number;
  currency: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  provider: string;
  /** Gateway payment id, or the UTR/receipt reference on hand-collected rows. */
  providerRef: string | null;
  /** CASH / BANK_TRANSFER / UPI / CARD / CHEQUE / OTHER — hand-collected rows only. */
  method: string | null;
  /** ISO date the money changed hands; falls back to the row's `createdAt`. */
  paidAt: string | null;
  note: string | null;
  receipt: string | null;
  createdAt: string;
}

/** Totals across every row matching the active filters, not just the current page. */
export interface AdminPaymentsSummaryDto {
  count: number;
  paidAmount: number;
  pendingAmount: number;
  refundedAmount: number;
  failedCount: number;
}

export interface AdminPaymentsPageDto {
    items: AdminPaymentRowDto[];
    total: number;
    page: number;
    pageSize: number;
    totalPages: number;
    summary: AdminPaymentsSummaryDto;
}

/**
 * One row of the admin stream-monitoring page: a tenant's stream URL with its
 * live inbound/outbound bitrate and per-viewer bandwidth split.
 */
export interface AdminMonitoringRowDto {
  id: string;
  name: string;
  tenantId: string;
  tenantName: string;
  /** Stored stream status; `live` is the MediaMTX-confirmed "publishing now". */
  status: StreamStatus;
  /** True when MediaMTX currently shows a publisher on this stream's path. */
  live: boolean;
  /**
   * Public RTMP ingest URL for this stream (`<base>/<ingestPath>`). Follows the
   * tenant-detail convention: never includes the stream key.
   */
  rtmpUrl: string;
  /** When the current publishing session started (ISO), null when offline. */
  sessionStartedAt: string | null;
  /** Ingest bitrate: encoder → MediaMTX, in kbps. */
  inputKbps: number | null;
  /** Egress bitrate: MediaMTX → viewers, in kbps. */
  outputKbps: number | null;
  /** Input + output over the most recent sample window, in kbps. */
  currentKbps: number | null;
  /** Highest input + output seen this session, in kbps. */
  peakKbps: number | null;
  /** Ingest bytes moved this session. */
  upBytes: number;
  /** Egress bytes moved this session. */
  downBytes: number;
  /** Distinct viewers watching right now. */
  viewers: number;
  /** Average egress share per current viewer (`outputKbps / viewers`), in kbps. */
  perViewerKbps: number | null;
}

/** One row of a tenant's own transaction history (their slice of the `payments` ledger). */
export interface TenantPaymentRowDto {
  id: string;
  planName: string | null;
  billingCycle: string | null;
  /** Stream this payment renewed when the order was stream-scoped (hybrid billing). */
  streamId: string | null;
  streamName: string | null;
  /** DECIMAL column, coerced to a number for display only. */
  amount: number;
  currency: string;
  status: 'PENDING' | 'PAID' | 'FAILED' | 'REFUNDED';
  provider: string;
  /** Gateway payment id, or the UTR/receipt reference on hand-collected rows. */
  providerRef: string | null;
  /** CASH / BANK_TRANSFER / UPI / CARD / CHEQUE / OTHER — hand-collected rows only. */
  method: string | null;
  /** ISO date the money changed hands; falls back to the row's `createdAt`. */
  paidAt: string | null;
  note: string | null;
  receipt: string | null;
  createdAt: string;
}

/** Totals across every row matching the active filters, not just the current page. */
export interface TenantPaymentsSummaryDto {
  count: number;
  paidAmount: number;
  pendingAmount: number;
  refundedAmount: number;
  failedCount: number;
}

export interface TenantPaymentsPageDto {
  items: TenantPaymentRowDto[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
  summary: TenantPaymentsSummaryDto;
}

export interface UpcomingRenewalDto {
  tenantId: string;
  tenantName: string;
  planName: string;
  /** Charge amount for the current billing cycle. */
  amount: number;
  currency: string;
  billingCycle: BillingCycle | null;
  /** ISO date the subscription renews (nextBillingAt, falls back to endAt). */
  renewsAt: string;
  /** Owner (or first member) email on the tenant, if any. */
  contactEmail: string | null;
  /** Owner (or first member) WhatsApp-able phone on the tenant, if any. */
  contactPhone: string | null;
}

export interface StreamingServerDto {
  id: string;
  name: string;
  host: string;
  region: string;
  status: 'ONLINE' | 'WARNING' | 'OFFLINE';
  activeStreams: number;
  capacity: number;
  cpuUsage: number | null;
  memoryUsage: number | null;
  bandwidthMbps: number | null;
}

/** Global templates used by renewal reminders (admin settings). */
export interface RenewalReminderSettings {
  /** WhatsApp message template. Placeholders: {tenant} {plan} {amount} {renewsOn} {days} {platform}. */
  whatsappTemplate: string;
  emailSubject: string;
  /** Email body template. Same placeholders as the WhatsApp template. */
  emailBody: string;
  /** Days ahead of renewal the admin dashboard flags a subscription. */
  reminderDays: number;
}

export interface EmailReminderResultDto {
  channel: 'email';
  /** `smtp` = sent via configured SMTP; `dev` = no SMTP configured, logged to API stdout. */
  sentVia: 'smtp' | 'dev';
}

export interface WhatsAppReminderResultDto {
  channel: 'whatsapp';
  /** Rendered message body — client opens it in a wa.me deep link. */
  message: string;
}

/** WhatsApp sender + outbound SMTP credentials (admin settings). */
export interface MessagingSettings {
  whatsappSender: string | null;
  smtpHost: string | null;
  smtpPort: number | null;
  smtpUser: string | null;
  /** '' on reads — the stored password is write-only. */
  smtpPassword: string;
  smtpFrom: string | null;
  /** True when host+port are set, so real emails go out instead of dev logs. */
  smtpConfigured: boolean;
}

/**
 * Razorpay gateway keys (admin settings). The stored key secret and webhook
 * secret are write-only — they are '' on reads.
 */
export interface RazorpaySettings {
  razorpayKeyId: string | null;
  razorpayKeySecret: string;
  razorpayWebhookSecret: string;
  /** ISO 4217 code, resolved DB → env → INR. */
  currency: string;
  /** True when key id + key secret are stored in the DB. */
  configured: boolean;
}

/* ------------------------------------------------------------------ */
/* Billing                                                             */
/* ------------------------------------------------------------------ */

export interface InvoiceDto {
  id: string;
  amount: number;
  currency: string;
  status: 'PAID' | 'FAILED' | 'REFUNDED' | 'PENDING';
  description: string | null;
  providerRef: string | null;
  createdAt: string;
}

export interface CreateOrderDto {
  planId: string;
  billingCycle: BillingCycle;
  /**
   * Hybrid billing: when set, the captured payment updates this stream's plan
   * and renewal date in addition to the tenant subscription. Omitted for
   * workspace-level subscription purchases.
   */
  streamId?: string;
}

export interface RazorpayOrderResponseDto {
  orderId: string;
  amount: number;
  currency: string;
  keyId: string;
  planId: string;
  billingCycle: BillingCycle;
  receipt: string;
  /** Echo of the stream this order renews, or null for a plain subscription. */
  streamId?: string | null;
}

/* ------------------------------------------------------------------ */
/* Streaming controller ↔ API contract                                 */
/* ------------------------------------------------------------------ */

export interface RelayTargetDto {
  destinationId: string;
  streamId: string;
  tenantId: string;
  rtmpUrl: string;
  streamKeyEncrypted: string;
  enabled: boolean;
}

export interface RelayJobDto {
  jobId: string;
  streamId: string;
  tenantId: string;
  /** MediaMTX RTMP ingest URL, e.g. rtmp://host:1935/<streamKey> */
  ingestUrl: string;
  /** Plan OUTBOUND cap PER destination (kbps); null/0 = unlimited. 2 Mbps × 5 destinations = 10 Mbps total egress */
  maxPerDestinationBitrateKbps?: number | null;
  targets: RelayTargetDto[];
}

export type RealtimeEventType =
  | 'stream.started'
  | 'stream.stopped'
  | 'stream.failed'
  | 'stream.health.updated'
  | 'destination.connected'
  | 'destination.disconnected'
  | 'destination.reconnecting'
  | 'destination.status'
  | 'notification.create'
  | 'tenant.suspended'
  | 'server.health'
  | 'chat.message'
  | 'chat.conversation.created'
  | 'chat.conversation.updated';

export interface RealtimeEvent<T = unknown> {
  type: RealtimeEventType;
  tenantId?: string;
  streamId?: string;
  destinationId?: string;
  conversationId?: string;
  timestamp: string;
  data: T;
}

/* ------------------------------------------------------------------ */
/* Chat                                                                */
/* ------------------------------------------------------------------ */

export type ConversationStatus = 'OPEN' | 'CLOSED' | 'ARCHIVED';

export interface ConversationDto {
  id: string;
  tenantId: string;
  tenantName: string;
  tenantSlug: string;
  status: ConversationStatus;
  subject: string | null;
  lastMessageAt: string | null;
  lastMessagePreview: string | null;
  unreadAdmin: number;
  unreadTenant: number;
  createdAt: string;
  updatedAt: string;
}

export interface MessageDto {
  id: string;
  conversationId: string;
  senderId: string;
  senderRole: UserRole;
  senderName: string | null;
  content: string;
  readAt: string | null;
  createdAt: string;
}

export interface CreateConversationDto {
  tenantId: string;
  subject?: string | null;
  initialMessage?: string | null;
}

export interface SendMessageDto {
  conversationId: string;
  content: string;
}

export interface ChatPaginationParams {
  page?: number;
  pageSize?: number;
  status?: ConversationStatus | 'ALL';
}

export interface StreamHealthPayload {
  streamId: string;
  bitrateKbps: number;
  fps: number;
  cpu: number;
  durationSeconds: number;
  bytes: number;
}

export interface DestinationHealthPayload {
  destinationId: string;
  status: DestinationStatus;
  bitrateKbps: number | null;
  fps: number | null;
  reconnectCount: number;
  lastError: string | null;
}

/* ------------------------------------------------------------------ */
/* Generic                                                             */
/* ------------------------------------------------------------------ */

export interface ApiErrorBody {
  statusCode: number;
  message: string | string[];
  error?: string;
  timestamp: string;
  path: string;
}

export interface PaginatedResponse<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}